/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShieldCheck, Upload, Loader2, CheckCircle2, Clock, XCircle, Info, Fingerprint, IdCard } from 'lucide-react';
import {
  getIdentityStatus, submitManualVerify, startSpid, startCie,
} from '@/lib/sprint5';
import { fileToBase64 } from '@/lib/sprint2';

const IdentityVerificationPage = () => {
  const [status, setStatus] = useState(null);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ doc_type: 'carta_identita', doc_number: '', image_base64: '', mime_type: '', dataUrl: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selProv, setSelProv] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const s = await getIdentityStatus();
      setStatus(s);
      setProviders(s.spid_providers || []);
    } catch (e) {
      // Non-fatal: user might not have identity data yet. Log in dev only.
      if (process.env.NODE_ENV !== 'production') {
         
        console.debug('[IdentityVerification] getIdentityStatus failed', e);
      }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error('Max 6 MB'); return; }
    const { base64, dataUrl, mime } = await fileToBase64(file);
    setForm((f) => ({ ...f, image_base64: base64, dataUrl, mime_type: mime }));
  };

  const submit = async () => {
    if (!form.image_base64 || !form.doc_number.trim()) { toast.error('Documento e numero richiesti'); return; }
    setSubmitting(true);
    try {
      await submitManualVerify(form);
      toast.success('Richiesta inviata — revisione entro 24-48h');
      setForm({ doc_type: 'carta_identita', doc_number: '', image_base64: '', mime_type: '', dataUrl: '' });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore invio');
    } finally { setSubmitting(false); }
  };

  const trySpid = async () => {
    if (!selProv) { toast.error('Seleziona un provider'); return; }
    try {
      const res = await startSpid(selProv);
      toast.info(res.note || 'SPID: attivabile dopo accreditamento AgID');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const tryCie = async () => {
    try {
      const res = await startCie();
      toast.info(res.note || 'CIE: attivabile dopo accreditamento');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const badge = status?.verified
    ? { label: 'Identità verificata', Icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' }
    : { label: 'Non verificata', Icon: XCircle, cls: 'bg-slate-100 text-slate-800 border-slate-200' };
  const BIcon = badge.Icon;
  const latest = status?.latest_request;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="identity-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" /> Verifica identità
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Verifica la tua identità per aumentare la fiducia dei tuoi interlocutori e sbloccare feature avanzate (firma referti, wound care remoto, care team).
        </p>
      </div>

      <Card><CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center border ${badge.cls}`}><BIcon className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-semibold text-sm">{badge.label}</div>
          <div className="text-xs text-muted-foreground">Level of Assurance: {status?.loa || 'none'}</div>
        </div>
        <Badge variant="outline">{status?.loa || 'none'}</Badge>
      </CardContent></Card>

      {latest?.status === 'pending' && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-700" />
            <div className="text-sm text-amber-900">Hai una richiesta in revisione (inviata il {new Date(latest.created_at).toLocaleDateString('it')}). Ti avviseremo entro 24-48h.</div>
          </CardContent>
        </Card>
      )}

      {/* SPID */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Fingerprint className="h-5 w-5 text-primary" /> Accedi con SPID</h2>
        <Card><CardContent className="p-4 space-y-3">
          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <Select value={selProv} onValueChange={setSelProv}>
              <SelectTrigger data-testid="spid-provider-select"><SelectValue placeholder="Scegli il tuo provider SPID" /></SelectTrigger>
              <SelectContent>{providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={trySpid} disabled={!selProv} data-testid="spid-start-btn">Continua</Button>
          </div>
          <div className="text-xs text-muted-foreground flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>SPID sarà attivo appena SanitaNexus completerà l'accreditamento come Service Provider AgID. Il codice è già pronto.</span>
          </div>
        </CardContent></Card>
      </section>

      {/* CIE */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><IdCard className="h-5 w-5 text-primary" /> Accedi con CIE</h2>
        <Card><CardContent className="p-4 space-y-3">
          <Button onClick={tryCie} className="w-full sm:w-auto" data-testid="cie-start-btn">
            <IdCard className="h-4 w-4" /> Accedi con Carta d'Identità Elettronica
          </Button>
          <div className="text-xs text-muted-foreground">CIE-OIDC in arrivo (richiede federazione IPZS).</div>
        </CardContent></Card>
      </section>

      {/* Manual verify */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Verifica manuale (già attiva)</h2>
        <Card><CardContent className="p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Tipo documento</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="identity-doctype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carta_identita">Carta d'identità</SelectItem>
                  <SelectItem value="cie">CIE (foto)</SelectItem>
                  <SelectItem value="patente">Patente</SelectItem>
                  <SelectItem value="passaporto">Passaporto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Numero documento</Label>
              <Input value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })} placeholder="AA1234567" data-testid="identity-docnum-input" />
            </div>
          </div>
          {form.dataUrl ? (
            <div className="relative">
              <img src={form.dataUrl} alt="Documento" className="max-h-56 rounded-lg border w-full object-contain" />
              <Button variant="secondary" size="sm" className="absolute top-2 right-2" onClick={() => setForm({ ...form, image_base64: '', dataUrl: '', mime_type: '' })}>Rimuovi</Button>
            </div>
          ) : (
            <Card className="p-4 border-dashed cursor-pointer text-center" onClick={() => inputRef.current?.click()} data-testid="identity-dropzone">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <div className="text-sm">Carica foto o scansione del documento</div>
              <div className="text-[10px] text-muted-foreground">JPEG / PNG / PDF · max 6 MB</div>
            </Card>
          )}
          <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <Button onClick={submit} disabled={submitting || latest?.status === 'pending'} data-testid="identity-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {latest?.status === 'pending' ? 'Richiesta già in revisione' : 'Invia per verifica manuale'}
          </Button>
        </CardContent></Card>
      </section>
    </div>
  );
};

export default IdentityVerificationPage;
